import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IHomepagePayload } from "../../../types/api/homepage";
import {  detailHomeData, saveHomeData } from "./callbacks";

export const useHomeDataSave = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IHomepagePayload>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IHomepagePayload>, unknown, IHomepagePayload> = {}
): UseMutationResult<TResponse<IHomepagePayload>, unknown, IHomepagePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IHomepagePayload>, unknown, IHomepagePayload>({
    mutationFn: async (data: IHomepagePayload) => {
      const response = await saveHomeData(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.HOMEPAGE_DETAIL, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists  
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useHomeDataDetail = (
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IHomepagePayload>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IHomepagePayload>, Error> & {
  refetchWithQuery: () => void;
} => {

  const queryResult = useQuery<TResponseWithQData<IHomepagePayload>, Error>({
    queryKey: [API_ENDPOINTS.HOMEPAGE_DETAIL],
    queryFn: async () => {
      const res = await detailHomeData();
      return res.data;
    },
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: () => {
      queryResult.refetch();
    },
  };
};

